import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { auth as authApi } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => void;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('jwt'));
  const loading = false;
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      const data = await authApi.me();
      setUser({
        id: data.id,
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      setError('Failed to fetch user profile');
      // Don't remove token here - the API interceptor handles token removal
    }
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    setToken(null);
    setUser(null);
    setError(null);
  }, []);

  useEffect(() => {
    const validateAndFetch = () => {
      if (token) {
        try {
          const decoded: { exp: number } = jwtDecode(token);
          if (decoded.exp * 1000 > Date.now()) {
            fetchUserProfile();
          } else {
            logout();
          }
        } catch {
          logout();
        }
      }
    };
    validateAndFetch();
  }, [token, fetchUserProfile, logout]);

  const loginWithToken = (newToken: string) => {
    try {
      const decoded: { exp: number } = jwtDecode(newToken);
      if (decoded.exp * 1000 <= Date.now()) {
        throw new Error('Received expired token');
      }
      localStorage.setItem('jwt', newToken);
      setToken(newToken);
    } catch (err) {
      console.error('Login with token failed:', err);
      logout();
    }
  };

  const login = async (email: string, password: string) => {
    // This function should not handle UI errors.
    // It's responsible for calling the API and updating the token.
    // Errors will be allowed to propagate up to the component that called it.
    const { token: newToken } = await authApi.login(email.trim().toLowerCase(), password);
    loginWithToken(newToken);
  };

  const value = {
    user,
    token,
    login,
    loginWithToken,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
